import { useEffect, useMemo, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AsciiCharField from './AsciiCharField';
import LinkedDevicesConfig, { LinkedDevice, parseLinkedDevices } from './LinkedDevicesConfig';
import PortConfigComponent, { buildDefaultPortConfig, PortConfig } from './PortConfig';

// Mirrors Weightrax.Domain.Enums.DirectionEnum
export const DIRECTION_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'In' },
  { value: 2, label: 'Out' },
  { value: 3, label: 'Both' }
];

// Mirrors Weightrax.Domain.Enums.WeightModeEnum. None is not offered: an indicator weighs one way or the other.
const WEIGHT_MODE_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'Gross' },
  { value: 2, label: 'Net' }
];

/** Reads a weight mode written by number (this editor) or by name (the Local API), defaulting to Gross. */
function parseWeightMode(value: unknown): number {
  if (typeof value === 'number') return WEIGHT_MODE_OPTIONS.some((o) => o.value === value) ? value : 1;
  if (typeof value === 'string') return WEIGHT_MODE_OPTIONS.find((o) => o.label.toLowerCase() === value.trim().toLowerCase())?.value ?? 1;
  return 1;
}

/** Reads a direction written by number (this editor) or by name (the Local API), defaulting to Both. */
function parseDirection(value: unknown): number {
  if (typeof value === 'number') return DIRECTION_OPTIONS.some((o) => o.value === value) ? value : 3;
  if (typeof value === 'string') return DIRECTION_OPTIONS.find((o) => o.label.toLowerCase() === value.trim().toLowerCase())?.value ?? 3;
  return 3;
}

// Mirrors Weightrax.Domain.Enums.LoadResetEnum.
export const LOAD_RESET_WHEN_PICKED = 1;
export const LOAD_RESET_AFTER_SET_TIME = 2;
export const DEFAULT_LOAD_RESET_MINUTES = 15;

/** Reads a load reset written by number (this editor) or by name (the Local API), defaulting to when picked. */
function parseLoadReset(value: unknown): number {
  if (value === LOAD_RESET_AFTER_SET_TIME || value === 'ResetAfterSetTime') return LOAD_RESET_AFTER_SET_TIME;
  return LOAD_RESET_WHEN_PICKED;
}

function parseLoadResetMinutes(value: unknown): number {
  return typeof value === 'number' && value >= 1 ? value : DEFAULT_LOAD_RESET_MINUTES;
}

export type WeighbridgeConfig = {
  portConfig: PortConfig;
  linkedDevices: LinkedDevice[];
  /** The traffic direction this indicator weighs — 1 In, 2 Out, 3 Both. */
  direction: number;
  /** Whether the indicator weighs the whole vehicle or the load alone — 1 Gross, 2 Net. */
  weightMode: number;
  /** How the feed is read: "Positional" at the feed and weight positions, or "Loadrite2180" for a loader scale's load totals. */
  protocol: string;
  /** When the POS stops offering a completed load — 1 when it is picked, 2 after loadResetMinutes. Only loader scales use it. */
  loadReset: number;
  loadResetMinutes: number;
  feedConfig: {
    startCharacter: string;
    endCharacter: string;
    length: number | null;
  };
  weightConfigs: Array<unknown>;
  sampleWeights: Array<unknown>;
  sampleFeed: string;
  feedPatterns: Array<unknown>;
  w1Settings: string;
  error: string;
};

type WeighbridgePeripheralConfigProps = {
  value: WeighbridgeConfig;
  onChange: (next: WeighbridgeConfig) => void;
  peripheralName?: string;
  onPeripheralNameChange?: (next: string) => void;
  templates?: WeighbridgePresetTemplate[];
  disabled?: boolean;
};

export type WeighbridgePresetTemplate = {
  id: number;
  label: string;
  configuration: unknown;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function getField(record: Record<string, unknown>, camel: string, pascal: string): unknown {
  if (record[camel] !== undefined) {
    return record[camel];
  }

  return record[pascal];
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function getWeightConfigField(record: Record<string, unknown>, camel: string, pascal: string): unknown {
  return getField(record, camel, pascal);
}

function patchWeightConfigField(camel: string, pascal: string, fieldValue: unknown): Record<string, unknown> {
  return {
    [camel]: fieldValue,
    [pascal]: fieldValue
  };
}

function getNestedWeightConfigRecord(row: Record<string, unknown>, camel: string, pascal: string): Record<string, unknown> {
  return asRecord(getWeightConfigField(row, camel, pascal));
}

function patchNestedWeightConfigField(
  row: Record<string, unknown>,
  camel: string,
  pascal: string,
  fieldCamel: string,
  fieldPascal: string,
  fieldValue: unknown
): Record<string, unknown> {
  const existing = getNestedWeightConfigRecord(row, camel, pascal);
  return patchWeightConfigField(camel, pascal, {
    ...existing,
    ...patchWeightConfigField(fieldCamel, fieldPascal, fieldValue)
  });
}

function toAdvancedModel(value: WeighbridgeConfig, peripheralName?: string): Record<string, unknown> {
  return {
    Name: peripheralName ?? '',
    PortConfig: value.portConfig,
    FeedConfig: {
      StartCharacter: value.feedConfig.startCharacter,
      EndCharacter: value.feedConfig.endCharacter,
      Length: value.feedConfig.length
    },
    WeightConfigs: value.weightConfigs,
    SampleWeights: value.sampleWeights,
    SampleFeed: value.sampleFeed,
    FeedPatterns: value.feedPatterns,
    W1Settings: value.w1Settings,
    Error: value.error,
    LinkedDevices: value.linkedDevices,
    Direction: value.direction,
    WeightMode: value.weightMode,
    Protocol: value.protocol,
    LoadReset: value.loadReset,
    LoadResetMinutes: value.loadResetMinutes
  };
}

function fromAdvancedModel(parsed: unknown): { config: WeighbridgeConfig; name?: string } {
  const source = asRecord(parsed);
  const feedConfig = asRecord(getField(source, 'feedConfig', 'FeedConfig'));

  return {
    config: {
      portConfig: buildDefaultPortConfig(getField(source, 'portConfig', 'PortConfig')),
      linkedDevices: parseLinkedDevices(source),
      direction: parseDirection(getField(source, 'direction', 'Direction')),
      weightMode: parseWeightMode(getField(source, 'weightMode', 'WeightMode')),
      protocol: asString(getField(source, 'protocol', 'Protocol'), 'Positional'),
      loadReset: parseLoadReset(getField(source, 'loadReset', 'LoadReset')),
      loadResetMinutes: parseLoadResetMinutes(getField(source, 'loadResetMinutes', 'LoadResetMinutes')),
      feedConfig: {
        startCharacter: asString(getField(feedConfig, 'startCharacter', 'StartCharacter')),
        endCharacter: asString(getField(feedConfig, 'endCharacter', 'EndCharacter')),
        length: asNumberOrNull(getField(feedConfig, 'length', 'Length'))
      },
      weightConfigs: Array.isArray(getField(source, 'weightConfigs', 'WeightConfigs'))
        ? (getField(source, 'weightConfigs', 'WeightConfigs') as Array<unknown>)
        : [],
      sampleWeights: Array.isArray(getField(source, 'sampleWeights', 'SampleWeights'))
        ? (getField(source, 'sampleWeights', 'SampleWeights') as Array<unknown>)
        : [],
      sampleFeed: asString(getField(source, 'sampleFeed', 'SampleFeed')),
      feedPatterns: Array.isArray(getField(source, 'feedPatterns', 'FeedPatterns'))
        ? (getField(source, 'feedPatterns', 'FeedPatterns') as Array<unknown>)
        : [],
      w1Settings: asString(getField(source, 'w1Settings', 'W1Settings')),
      error: asString(getField(source, 'error', 'Error'))
    },
    name: asString(getField(source, 'name', 'Name'))
  };
}

export function buildDefaultWeighbridgeConfig(base?: unknown): WeighbridgeConfig {
  const source = asRecord(base);
  const sourceFeedConfig = asRecord(getField(source, 'feedConfig', 'FeedConfig'));

  return {
    portConfig: buildDefaultPortConfig(getField(source, 'portConfig', 'PortConfig')),
    linkedDevices: parseLinkedDevices(source),
    direction: parseDirection(getField(source, 'direction', 'Direction')),
    weightMode: parseWeightMode(getField(source, 'weightMode', 'WeightMode')),
    protocol: asString(getField(source, 'protocol', 'Protocol'), 'Positional'),
    loadReset: parseLoadReset(getField(source, 'loadReset', 'LoadReset')),
    loadResetMinutes: parseLoadResetMinutes(getField(source, 'loadResetMinutes', 'LoadResetMinutes')),
    feedConfig: {
      startCharacter: asString(getField(sourceFeedConfig, 'startCharacter', 'StartCharacter')),
      endCharacter: asString(getField(sourceFeedConfig, 'endCharacter', 'EndCharacter')),
      length: asNumberOrNull(getField(sourceFeedConfig, 'length', 'Length'))
    },
    weightConfigs: Array.isArray(getField(source, 'weightConfigs', 'WeightConfigs'))
      ? (getField(source, 'weightConfigs', 'WeightConfigs') as Array<unknown>)
      : [],
    sampleWeights: Array.isArray(getField(source, 'sampleWeights', 'SampleWeights'))
      ? (getField(source, 'sampleWeights', 'SampleWeights') as Array<unknown>)
      : [],
    sampleFeed: asString(getField(source, 'sampleFeed', 'SampleFeed')),
    feedPatterns: Array.isArray(getField(source, 'feedPatterns', 'FeedPatterns'))
      ? (getField(source, 'feedPatterns', 'FeedPatterns') as Array<unknown>)
      : [],
    w1Settings: asString(getField(source, 'w1Settings', 'W1Settings')),
    error: asString(getField(source, 'error', 'Error'))
  };
}

const WEIGHT_CONFIG_SECTIONS = [
  { key: 'motion', label: 'Motion' },
  { key: 'mode', label: 'Mode' },
  { key: 'unit', label: 'Unit' },
  { key: 'zero', label: 'Zero' }
] as const;

type WeightConfigSectionKey = (typeof WEIGHT_CONFIG_SECTIONS)[number]['key'];

export default function WeighbridgePeripheralConfig({
  value,
  onChange,
  peripheralName,
  onPeripheralNameChange,
  templates = [],
  disabled = false
}: WeighbridgePeripheralConfigProps) {
  const [advancedMode, setAdvancedMode] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [visibleSections, setVisibleSections] = useState<Record<WeightConfigSectionKey, boolean>>({
    motion: true,
    mode: true,
    unit: true,
    zero: true
  });

  const toggleSection = (key: WeightConfigSectionKey) => {
    setVisibleSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const advancedJson = useMemo(() => JSON.stringify(toAdvancedModel(value, peripheralName), null, 2), [value, peripheralName]);
  const [jsonText, setJsonText] = useState(advancedJson);

  const weightConfigRows = useMemo(() => {
    return value.weightConfigs.map((item) => asRecord(item));
  }, [value.weightConfigs]);

  useEffect(() => {
    if (templates.length === 0) {
      setSelectedTemplateId(null);
      return;
    }

    setSelectedTemplateId((current) => {
      if (current != null && templates.some((template) => template.id === current)) {
        return current;
      }

      return templates[0].id;
    });
  }, [templates]);

  const handleModeToggle = (checked: boolean) => {
    setAdvancedMode(checked);
    setJsonError(null);
    if (checked) {
      setJsonText(advancedJson);
    }
  };

  const handleApplyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      const next = fromAdvancedModel(parsed);

      onChange(next.config);
      if (typeof next.name === 'string' && onPeripheralNameChange) {
        onPeripheralNameChange(next.name);
      }

      setJsonError(null);
    } catch {
      setJsonError('Configuration JSON is invalid. Fix the JSON and try again.');
    }
  };

  const handleApplyTemplate = () => {
    if (selectedTemplateId == null) {
      return;
    }

    const template = templates.find((item) => item.id === selectedTemplateId);
    if (!template) {
      return;
    }

    try {
      const next = fromAdvancedModel(template.configuration);
      // A template describes the indicator model, not where this one is used, so the links are kept.
      next.config = { ...next.config, linkedDevices: value.linkedDevices };
      onChange(next.config);
      if (typeof next.name === 'string' && onPeripheralNameChange) {
        onPeripheralNameChange(next.name);
      }
      setJsonError(null);
      setJsonText(JSON.stringify(toAdvancedModel(next.config, next.name ?? peripheralName), null, 2));
    } catch {
      setJsonError('Selected template is invalid and could not be applied.');
    }
  };

  const updateFeedConfigCharacter = (key: 'startCharacter' | 'endCharacter', asciiCode: number | null) => {
    onChange({
      ...value,
      feedConfig: {
        ...value.feedConfig,
        [key]: asciiCode !== null ? String.fromCharCode(asciiCode) : ''
      }
    });
  };

  const updateWeightConfigRow = (index: number, patch: Record<string, unknown>) => {
    const nextWeightConfigs = value.weightConfigs.map((item, itemIndex) => {
      if (itemIndex !== index) {
        return item;
      }

      return {
        ...asRecord(item),
        ...patch
      };
    });

    onChange({
      ...value,
      weightConfigs: nextWeightConfigs
    });
  };

  const removeWeightConfigRow = (index: number) => {
    onChange({
      ...value,
      weightConfigs: value.weightConfigs.filter((_, itemIndex) => itemIndex !== index)
    });
  };

  const addWeightConfigRow = () => {
    const newRow = {
      ...patchWeightConfigField('name', 'Name', `Weight ${value.weightConfigs.length + 1}`),
      ...patchWeightConfigField('startPosition', 'StartPosition', 0),
      ...patchWeightConfigField('endPosition', 'EndPosition', 0),
      ...patchWeightConfigField('length', 'Length', 0),
      ...patchWeightConfigField('noDecimalPlaces', 'NoDecimalPlaces', 0),
      ...patchWeightConfigField('isTotal', 'IsTotal', false),
      ...patchWeightConfigField('motion', 'Motion', {
        ...patchWeightConfigField('position', 'Position', 0),
        ...patchWeightConfigField('matchValue', 'MatchValue', ''),
        ...patchWeightConfigField('mapsTo', 'MapsTo', '')
      }),
      ...patchWeightConfigField('mode', 'Mode', {
        ...patchWeightConfigField('position', 'Position', 0),
        ...patchWeightConfigField('matchValue', 'MatchValue', ''),
        ...patchWeightConfigField('mapsTo', 'MapsTo', '')
      }),
      ...patchWeightConfigField('unit', 'Unit', {
        ...patchWeightConfigField('position', 'Position', 0),
        ...patchWeightConfigField('matchValue', 'MatchValue', ''),
        ...patchWeightConfigField('mapsTo', 'MapsTo', '')
      }),
      ...patchWeightConfigField('zero', 'Zero', {
        ...patchWeightConfigField('position', 'Position', 0),
        ...patchWeightConfigField('matchValue', 'MatchValue', ''),
        ...patchWeightConfigField('mapsTo', 'MapsTo', '')
      })
    };

    onChange({
      ...value,
      weightConfigs: [...value.weightConfigs, newRow]
    });
  };

  return (
    <Stack spacing={2}>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            fullWidth
            select
            label="Preset Template"
            value={selectedTemplateId ?? ''}
            onChange={(event) => {
              const next = Number(event.target.value);
              setSelectedTemplateId(Number.isFinite(next) ? next : null);
            }}
            disabled={disabled || templates.length === 0}
          >
            {templates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Button fullWidth variant="outlined" onClick={handleApplyTemplate} disabled={disabled || selectedTemplateId == null}>
            Apply Template
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Weighbridge Configuration
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Simple
          </Typography>
          <Switch checked={advancedMode} onChange={(_, checked) => handleModeToggle(checked)} disabled={disabled} />
          <Typography variant="body2" color="text.secondary">
            Advanced
          </Typography>
        </Box>
      </Box>

      {!advancedMode ? (
        <Grid container spacing={2}>
          <PortConfigComponent value={value.portConfig} onChange={(next) => onChange({ ...value, portConfig: next })} disabled={disabled} />
          <Grid size={{ xs: 10 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 4, md: 2 }}>
                <AsciiCharField
                  label="Feed start"
                  value={value.feedConfig.startCharacter ? value.feedConfig.startCharacter.charCodeAt(0) : null}
                  onChange={(n) => updateFeedConfigCharacter('startCharacter', n)}
                  disabled={disabled}
                />
              </Grid>

              <Grid size={{ xs: 4, md: 2 }}>
                <AsciiCharField
                  label="Feed end"
                  value={value.feedConfig.endCharacter ? value.feedConfig.endCharacter.charCodeAt(0) : null}
                  onChange={(n) => updateFeedConfigCharacter('endCharacter', n)}
                  disabled={disabled}
                />
              </Grid>

              <Grid size={{ xs: 4, md: 2 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Feed Length"
                  value={value.feedConfig.length ?? ''}
                  onChange={(event) => {
                    const next = event.target.value.trim();
                    onChange({
                      ...value,
                      feedConfig: {
                        ...value.feedConfig,
                        length: next === '' ? null : Number(next)
                      }
                    });
                  }}
                  disabled={disabled}
                />
              </Grid>

              <Grid size={{ xs: 4, md: 2 }}>
                <TextField
                  fullWidth
                  select
                  label="Direction"
                  value={value.direction}
                  onChange={(event) => onChange({ ...value, direction: Number(event.target.value) })}
                  disabled={disabled}
                >
                  {DIRECTION_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 4, md: 2 }}>
                <TextField
                  fullWidth
                  select
                  label="Weight mode"
                  value={value.weightMode}
                  onChange={(event) => onChange({ ...value, weightMode: Number(event.target.value) })}
                  helperText={value.weightMode === 2 ? 'Weighs the load alone' : 'Weighs vehicle in & out'}
                  disabled={disabled}
                >
                  {WEIGHT_MODE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Weight Config Options
                </Typography>
                <Button variant="outlined" size="small" onClick={addWeightConfigRow} disabled={disabled}>
                  Add Row
                </Button>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" color="text.secondary">
                  Columns:
                </Typography>
                {WEIGHT_CONFIG_SECTIONS.map(({ key, label }) => (
                  <Chip
                    key={key}
                    label={label}
                    size="small"
                    color={visibleSections[key] ? 'primary' : 'default'}
                    variant={visibleSections[key] ? 'filled' : 'outlined'}
                    onClick={() => toggleSection(key)}
                    disabled={disabled}
                  />
                ))}
              </Stack>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Start</TableCell>
                      <TableCell>End</TableCell>
                      <TableCell>Length</TableCell>
                      <TableCell>Decimals</TableCell>
                      {visibleSections.motion && <TableCell>Motion</TableCell>}
                      {visibleSections.mode && <TableCell>Mode</TableCell>}
                      {visibleSections.unit && <TableCell>Unit</TableCell>}
                      {visibleSections.zero && <TableCell>Zero</TableCell>}
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {weightConfigRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7 + Object.values(visibleSections).filter(Boolean).length}>
                          <Typography variant="body2" color="text.secondary">
                            No weight configs defined yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      weightConfigRows.map((row, index) => {
                        const name = asString(getWeightConfigField(row, 'name', 'Name'));
                        const startPosition = asNumberOrNull(getWeightConfigField(row, 'startPosition', 'StartPosition'));
                        const endPosition = asNumberOrNull(getWeightConfigField(row, 'endPosition', 'EndPosition'));
                        const length = asNumberOrNull(getWeightConfigField(row, 'length', 'Length'));
                        const noDecimalPlaces = asNumberOrNull(getWeightConfigField(row, 'noDecimalPlaces', 'NoDecimalPlaces'));
                        const isTotal = asBoolean(getWeightConfigField(row, 'isTotal', 'IsTotal'));
                        const motion = getNestedWeightConfigRecord(row, 'motion', 'Motion');
                        const mode = getNestedWeightConfigRecord(row, 'mode', 'Mode');
                        const unit = getNestedWeightConfigRecord(row, 'unit', 'Unit');
                        const zero = getNestedWeightConfigRecord(row, 'zero', 'Zero');

                        return (
                          <TableRow key={`${index}-${name}`}>
                            <TableCell sx={{ minWidth: 160 }}>
                              <TextField
                                size="small"
                                fullWidth
                                value={name}
                                onChange={(event) =>
                                  updateWeightConfigRow(index, patchWeightConfigField('name', 'Name', event.target.value))
                                }
                                disabled={disabled}
                              />
                            </TableCell>
                            <TableCell sx={{ width: 70 }}>
                              <Checkbox
                                checked={isTotal}
                                onChange={(event) =>
                                  updateWeightConfigRow(index, patchWeightConfigField('isTotal', 'IsTotal', event.target.checked))
                                }
                                disabled={disabled}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={startPosition ?? ''}
                                onChange={(event) => {
                                  const next = event.target.value.trim();
                                  updateWeightConfigRow(
                                    index,
                                    patchWeightConfigField('startPosition', 'StartPosition', next === '' ? null : Number(next))
                                  );
                                }}
                                disabled={disabled}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={endPosition ?? ''}
                                onChange={(event) => {
                                  const next = event.target.value.trim();
                                  updateWeightConfigRow(
                                    index,
                                    patchWeightConfigField('endPosition', 'EndPosition', next === '' ? null : Number(next))
                                  );
                                }}
                                disabled={disabled}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={length ?? ''}
                                onChange={(event) => {
                                  const next = event.target.value.trim();
                                  updateWeightConfigRow(
                                    index,
                                    patchWeightConfigField('length', 'Length', next === '' ? null : Number(next))
                                  );
                                }}
                                disabled={disabled}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={noDecimalPlaces ?? ''}
                                onChange={(event) => {
                                  const next = event.target.value.trim();
                                  updateWeightConfigRow(
                                    index,
                                    patchWeightConfigField('noDecimalPlaces', 'NoDecimalPlaces', next === '' ? null : Number(next))
                                  );
                                }}
                                disabled={disabled}
                              />
                            </TableCell>
                            {visibleSections.motion && (
                              <TableCell sx={{ minWidth: 220 }}>
                                <Stack direction="row" spacing={1}>
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Pos"
                                    value={asNumberOrNull(getWeightConfigField(motion, 'position', 'Position')) ?? ''}
                                    onChange={(event) => {
                                      const next = event.target.value.trim();
                                      updateWeightConfigRow(
                                        index,
                                        patchNestedWeightConfigField(
                                          row,
                                          'motion',
                                          'Motion',
                                          'position',
                                          'Position',
                                          next === '' ? null : Number(next)
                                        )
                                      );
                                    }}
                                    disabled={disabled}
                                  />
                                  <TextField
                                    size="small"
                                    label="Match"
                                    value={asString(getWeightConfigField(motion, 'matchValue', 'MatchValue'))}
                                    onChange={(event) =>
                                      updateWeightConfigRow(
                                        index,
                                        patchNestedWeightConfigField(
                                          row,
                                          'motion',
                                          'Motion',
                                          'matchValue',
                                          'MatchValue',
                                          event.target.value
                                        )
                                      )
                                    }
                                    disabled={disabled}
                                  />
                                </Stack>
                              </TableCell>
                            )}
                            {visibleSections.mode && (
                              <TableCell sx={{ minWidth: 220 }}>
                                <Stack direction="row" spacing={1}>
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Pos"
                                    value={asNumberOrNull(getWeightConfigField(mode, 'position', 'Position')) ?? ''}
                                    onChange={(event) => {
                                      const next = event.target.value.trim();
                                      updateWeightConfigRow(
                                        index,
                                        patchNestedWeightConfigField(
                                          row,
                                          'mode',
                                          'Mode',
                                          'position',
                                          'Position',
                                          next === '' ? null : Number(next)
                                        )
                                      );
                                    }}
                                    disabled={disabled}
                                  />
                                  <TextField
                                    size="small"
                                    label="Match"
                                    value={asString(getWeightConfigField(mode, 'matchValue', 'MatchValue'))}
                                    onChange={(event) =>
                                      updateWeightConfigRow(
                                        index,
                                        patchNestedWeightConfigField(row, 'mode', 'Mode', 'matchValue', 'MatchValue', event.target.value)
                                      )
                                    }
                                    disabled={disabled}
                                  />
                                </Stack>
                              </TableCell>
                            )}
                            {visibleSections.unit && (
                              <TableCell sx={{ minWidth: 320 }}>
                                <Stack direction="row" spacing={1}>
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Pos"
                                    value={asNumberOrNull(getWeightConfigField(unit, 'position', 'Position')) ?? ''}
                                    onChange={(event) => {
                                      const next = event.target.value.trim();
                                      updateWeightConfigRow(
                                        index,
                                        patchNestedWeightConfigField(
                                          row,
                                          'unit',
                                          'Unit',
                                          'position',
                                          'Position',
                                          next === '' ? null : Number(next)
                                        )
                                      );
                                    }}
                                    disabled={disabled}
                                  />
                                  <TextField
                                    size="small"
                                    label="Match"
                                    value={asString(getWeightConfigField(unit, 'matchValue', 'MatchValue'))}
                                    onChange={(event) =>
                                      updateWeightConfigRow(
                                        index,
                                        patchNestedWeightConfigField(row, 'unit', 'Unit', 'matchValue', 'MatchValue', event.target.value)
                                      )
                                    }
                                    disabled={disabled}
                                  />
                                  <TextField
                                    size="small"
                                    label="Maps To"
                                    value={asString(getWeightConfigField(unit, 'mapsTo', 'MapsTo'))}
                                    onChange={(event) =>
                                      updateWeightConfigRow(
                                        index,
                                        patchNestedWeightConfigField(row, 'unit', 'Unit', 'mapsTo', 'MapsTo', event.target.value)
                                      )
                                    }
                                    disabled={disabled}
                                  />
                                </Stack>
                              </TableCell>
                            )}
                            {visibleSections.zero && (
                              <TableCell sx={{ minWidth: 320 }}>
                                <Stack direction="row" spacing={1}>
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Pos"
                                    value={asNumberOrNull(getWeightConfigField(zero, 'position', 'Position')) ?? ''}
                                    onChange={(event) => {
                                      const next = event.target.value.trim();
                                      updateWeightConfigRow(
                                        index,
                                        patchNestedWeightConfigField(
                                          row,
                                          'zero',
                                          'Zero',
                                          'position',
                                          'Position',
                                          next === '' ? null : Number(next)
                                        )
                                      );
                                    }}
                                    disabled={disabled}
                                  />
                                  <TextField
                                    size="small"
                                    label="Match"
                                    value={asString(getWeightConfigField(zero, 'matchValue', 'MatchValue'))}
                                    onChange={(event) =>
                                      updateWeightConfigRow(
                                        index,
                                        patchNestedWeightConfigField(row, 'zero', 'Zero', 'matchValue', 'MatchValue', event.target.value)
                                      )
                                    }
                                    disabled={disabled}
                                  />
                                  <TextField
                                    size="small"
                                    label="Maps To"
                                    value={asString(getWeightConfigField(zero, 'mapsTo', 'MapsTo'))}
                                    onChange={(event) =>
                                      updateWeightConfigRow(
                                        index,
                                        patchNestedWeightConfigField(row, 'zero', 'Zero', 'mapsTo', 'MapsTo', event.target.value)
                                      )
                                    }
                                    disabled={disabled}
                                  />
                                </Stack>
                              </TableCell>
                            )}
                            <TableCell align="right" sx={{ width: 110 }}>
                              <Button color="error" size="small" onClick={() => removeWeightConfigRow(index)} disabled={disabled}>
                                Remove
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 12 }}>
            <TextField
              fullWidth
              label="Sample Feed"
              value={value.sampleFeed}
              onChange={(event) => onChange({ ...value, sampleFeed: event.target.value })}
              disabled={disabled}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <LinkedDevicesConfig
              value={value.linkedDevices}
              onChange={(next) => onChange({ ...value, linkedDevices: next })}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      ) : (
        <Stack spacing={1.5}>
          <TextField
            fullWidth
            multiline
            minRows={18}
            label="Configuration JSON"
            value={jsonText}
            onChange={(event) => setJsonText(event.target.value)}
            disabled={disabled}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={handleApplyJson} disabled={disabled}>
              Apply JSON
            </Button>
          </Box>
          {jsonError ? <Alert severity="error">{jsonError}</Alert> : null}
        </Stack>
      )}
    </Stack>
  );
}
